import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import axiosInstance from '../../api/axios';
import { getErrorDetail, localizeErrorDetail } from '../../utils/errorDetail';

// ─── Styled Components ────────────────────────────────────────────────────────

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const PageTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1C1511;
  margin-bottom: 28px;
  font-family: Georgia, serif;
`;

const Section = styled.div`
  background: #F5F0E8;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 28px;
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1C1511;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid #E8DDD0;
  font-family: Georgia, serif;
`;

const FieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
`;

const FieldLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #1C1511;
  min-width: 160px;
`;

const NumberInput = styled.input`
  width: 120px;
  padding: 8px 12px;
  border: 1px solid #E8DDD0;
  border-radius: 6px;
  font-size: 14px;
  text-align: center;
  background: #FFFFFF;
  color: #1C1511;

  &:focus {
    outline: none;
    border-color: #D4A017;
    box-shadow: 0 0 0 3px rgba(212, 160, 23, 0.1);
  }
`;

const TextInput = styled.input`
  flex: 1;
  max-width: 320px;
  padding: 8px 12px;
  border: 1px solid #E8DDD0;
  border-radius: 6px;
  font-size: 14px;
  background: #FFFFFF;
  color: #1C1511;

  &:focus {
    outline: none;
    border-color: #D4A017;
    box-shadow: 0 0 0 3px rgba(212, 160, 23, 0.1);
  }
`;

const ActionGroup = styled.div`
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SaveButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #D4A017;
  color: #1C1511;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  margin-top: 8px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(212, 160, 23, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const Toast = styled.div<{ variant: 'success' | 'error' }>`
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  margin-top: 16px;
  background: ${props => props.variant === 'success' ? '#d1fae5' : '#fee2e2'};
  color: ${props => props.variant === 'success' ? '#065f46' : '#991b1b'};
`;

const LoadingText = styled.p`
  font-size: 14px;
  color: #6B5E4E;
  text-align: center;
  padding: 24px 0;
`;

const ComingSoonText = styled.p`
  font-size: 14px;
  color: #6B5E4E;
  font-style: italic;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #1C1511;
  cursor: pointer;
  margin-bottom: 16px;
`;

// ─── Types ────────────────────────────────────────────────────────────────────

interface QueueSettings {
  current_number: number;
  wrap_limit: number;
  reset_base_number?: number;
  timezone?: string | null;
  last_reset_at?: string | null;
  last_reset_date?: string | null;
}

type XpyunLabelLanguage = 'en' | 'zh';

interface XpyunPrintSettings {
  enabled: boolean;
  label_language?: XpyunLabelLanguage;
}

interface TestActionResponse {
  success: boolean;
  detail: string;
}

// ─── Queue Counter Section ────────────────────────────────────────────────────

const clampInt = (raw: string, min: number, max: number, fallback: number): number => {
  const parsed = parseInt(raw, 10);
  if (Number.isNaN(parsed)) return fallback;
  return Math.min(Math.max(parsed, min), max);
};

// Backend datetimes may arrive without a UTC offset; treat those as UTC.
const formatLastReset = (iso: string, timezone?: string | null): string => {
  const normalized = /(Z|[+-]\d{2}:?\d{2})$/.test(iso) ? iso : `${iso}Z`;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return iso;
  try {
    return date.toLocaleString(undefined, timezone ? { timeZone: timezone } : undefined);
  } catch {
    return date.toLocaleString();
  }
};

const QueueCounterSection: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [currentNumber, setCurrentNumber] = useState<string>('0');
  const [wrapLimit, setWrapLimit] = useState<string>('999');
  const [timezone, setTimezone] = useState<string>('');
  const [toast, setToast] = useState<{ variant: 'success' | 'error'; message: string } | null>(null);

  const { data: queueData, isLoading } = useQuery<QueueSettings>({
    queryKey: ['adminQueueSettings'],
    queryFn: async () => {
      const res = await axiosInstance.get<QueueSettings>('/admin/settings/queue');
      return res.data;
    },
  });

  useEffect(() => {
    if (queueData) {
      setCurrentNumber(String(queueData.current_number));
      setWrapLimit(String(queueData.wrap_limit));
      setTimezone(queueData.timezone ?? '');
    }
  }, [queueData]);

  const saveMutation = useMutation({
    mutationFn: async (payload: Partial<QueueSettings>) => {
      const res = await axiosInstance.put<QueueSettings>('/admin/settings/queue', payload);
      return res.data;
    },
    onSuccess: (data) => {
      setCurrentNumber(String(data.current_number));
      setWrapLimit(String(data.wrap_limit));
      setTimezone(data.timezone ?? '');
      queryClient.invalidateQueries({ queryKey: ['adminQueueSettings'] });
      setToast({ variant: 'success', message: t('admin.settings.saveSuccess') });
      setTimeout(() => setToast(null), 3000);
    },
    onError: (err) => {
      setToast({ variant: 'error', message: localizeErrorDetail(getErrorDetail(err), t) || t('admin.settings.saveError') });
      setTimeout(() => setToast(null), 4000);
    },
  });

  const handleSave = () => {
    const payload: Partial<QueueSettings> = {
      current_number: clampInt(currentNumber, 0, 9999, 0),
      wrap_limit: clampInt(wrapLimit, 1, 9999, 1),
    };
    // Only send timezone when it was actually changed: a tz change re-stamps
    // last_reset_date server-side, so an untouched field must not be re-sent.
    const trimmedTz = timezone.trim();
    if (trimmedTz && trimmedTz !== (queueData?.timezone ?? '')) {
      payload.timezone = trimmedTz;
    }
    saveMutation.mutate(payload);
  };

  if (isLoading) {
    return <LoadingText>{t('admin.settings.loadingQueueSettings')}</LoadingText>;
  }

  return (
    <>
      <FieldRow>
        <FieldLabel htmlFor="currentNumber">{t('admin.settings.currentNumberLabel')}</FieldLabel>
        <NumberInput
          id="currentNumber"
          type="number"
          min={0}
          max={9999}
          value={currentNumber}
          onChange={e => setCurrentNumber(e.target.value)}
          onBlur={() => setCurrentNumber(String(clampInt(currentNumber, 0, 9999, 0)))}
        />
      </FieldRow>
      {queueData && (
        <ComingSoonText data-testid="queue-next-number">
          {t('admin.settings.queueNextNumberLine', {
            number:
              queueData.current_number + 1 > queueData.wrap_limit ? 1 : queueData.current_number + 1,
          })}
          {queueData.current_number <= (queueData.reset_base_number ?? 0) && ` ${t('admin.settings.queueNoneIssuedHint')}`}
        </ComingSoonText>
      )}
      <FieldRow>
        <FieldLabel htmlFor="wrapLimit">{t('admin.settings.wrapLimitLabel')}</FieldLabel>
        <NumberInput
          id="wrapLimit"
          type="number"
          min={1}
          max={9999}
          value={wrapLimit}
          onChange={e => setWrapLimit(e.target.value)}
          onBlur={() => setWrapLimit(String(clampInt(wrapLimit, 1, 9999, 1)))}
        />
      </FieldRow>
      <FieldRow>
        <FieldLabel htmlFor="queueTimezone">{t('admin.settings.queueTimezoneLabel')}</FieldLabel>
        <TextInput
          id="queueTimezone"
          data-testid="queue-timezone"
          type="text"
          value={timezone}
          placeholder="Europe/London"
          onChange={e => setTimezone(e.target.value)}
        />
      </FieldRow>
      <ComingSoonText data-testid="queue-last-reset">
        {queueData?.last_reset_at
          ? t('admin.settings.queueLastResetLine', {
              time: formatLastReset(queueData.last_reset_at, queueData.timezone),
              timezone: queueData.timezone ?? '',
            })
          : t('admin.settings.queueNoResetYet')}
      </ComingSoonText>
      <SaveButton onClick={handleSave} disabled={saveMutation.isPending}>
        {saveMutation.isPending ? t('admin.settings.savingButton') : t('admin.settings.saveButton')}
      </SaveButton>
      {toast && (
        <Toast variant={toast.variant}>{toast.message}</Toast>
      )}
    </>
  );
};

// ─── Print Labels Section ─────────────────────────────────────────────────────

const XpyunPrintSection: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState<boolean>(true);
  const [toast, setToast] = useState<{ variant: 'success' | 'error'; message: string } | null>(null);
  const [labelLanguage, setLabelLanguage] = useState<XpyunLabelLanguage>('en');
  const inFlightRef = useRef<boolean>(false);
  const changedRef = useRef<{ field: 'enabled' | 'language'; previous: boolean | string } | null>(null);

  const { data: printData, isLoading } = useQuery<XpyunPrintSettings>({
    queryKey: ['adminXpyunPrintSettings'],
    queryFn: async () => {
      const res = await axiosInstance.get<XpyunPrintSettings>('/admin/settings/xpyun-print');
      return res.data;
    },
  });

  useEffect(() => {
    if (printData) {
      setEnabled(printData.enabled ?? true);
      setLabelLanguage(printData.label_language === 'zh' ? 'zh' : 'en');
    }
  }, [printData]);

  const saveMutation = useMutation<XpyunPrintSettings, unknown, XpyunPrintSettings>({
    mutationFn: async (payload: XpyunPrintSettings) => {
      const res = await axiosInstance.put<XpyunPrintSettings>('/admin/settings/xpyun-print', payload);
      return res.data;
    },
    onSuccess: (data) => {
      inFlightRef.current = false;
      setEnabled(data.enabled ?? true);
      if (data.label_language) setLabelLanguage(data.label_language === 'zh' ? 'zh' : 'en');
      queryClient.invalidateQueries({ queryKey: ['adminXpyunPrintSettings'] });
      setToast({ variant: 'success', message: t('admin.settings.printSettingsSaveSuccess') });
      setTimeout(() => setToast(null), 3000);
    },
    onError: (_err, variables) => {
      inFlightRef.current = false;
      // Deliberately NOT reading the `context` returned by onMutate here: React Query
      // re-registers this mutation's callbacks against the latest render's closures on
      // every render, so by the time onError/onMutate actually fire (after the PUT
      // settles), a point-in-time `enabled` snapshot captured in onMutate can already
      // reflect handleToggle's own optimistic setEnabled(next) update rather than the
      // value from before the click (confirmed empirically; see testing notes). Since
      // `handleToggle` only ever flips the checkbox and this control is disabled while
      // saveMutation.isPending (so no overlapping toggles can interleave), the correct
      // "undo my own delta" revert is simply the negation of what THIS call attempted --
      // `variables` is the exact, call-scoped payload passed to this mutate() invocation
      // and is never subject to the same closure-staleness.
      // Revert ONLY the field this call changed (tracked in changedRef), so a failed
      // language change never flips the checkbox and vice versa.
      const changed = changedRef.current;
      if (changed?.field === 'language') {
        setLabelLanguage(changed.previous as XpyunLabelLanguage);
      } else {
        setEnabled(!variables.enabled);
      }
      setToast({ variant: 'error', message: t('admin.settings.printSettingsSaveError') });
      setTimeout(() => setToast(null), 4000);
    },
  });

  const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (inFlightRef.current) return;
    const next = e.target.checked;
    inFlightRef.current = true;
    changedRef.current = { field: 'enabled', previous: !next };
    setEnabled(next);
    saveMutation.mutate({ enabled: next, label_language: labelLanguage });
  };

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (inFlightRef.current) return;
    const next: XpyunLabelLanguage = e.target.value === 'zh' ? 'zh' : 'en';
    inFlightRef.current = true;
    changedRef.current = { field: 'language', previous: labelLanguage };
    setLabelLanguage(next);
    saveMutation.mutate({ enabled, label_language: next });
  };

  if (isLoading) {
    return <LoadingText>{t('admin.settings.loadingPrintSettings')}</LoadingText>;
  }

  return (
    <>
      <CheckboxLabel htmlFor="xpyunPrintEnabled">
        <input
          id="xpyunPrintEnabled"
          type="checkbox"
          checked={enabled}
          disabled={saveMutation.isPending}
          onChange={handleToggle}
        />
        {t('admin.settings.printLabelsToggleLabel')}
      </CheckboxLabel>
      <FieldRow>
        <FieldLabel htmlFor="xpyunLabelLanguage">{t('admin.settings.printLabelLanguageLabel')}</FieldLabel>
        <select
          id="xpyunLabelLanguage"
          data-testid="xpyun-label-language"
          value={labelLanguage}
          disabled={saveMutation.isPending}
          onChange={handleLanguageChange}
        >
          <option value="en">{t('admin.settings.printLabelLanguageEnglish')}</option>
          <option value="zh">{t('admin.settings.printLabelLanguageChinese')}</option>
        </select>
      </FieldRow>
      <ComingSoonText>{t('admin.settings.printLabelLanguageHelp')}</ComingSoonText>
      {toast && (
        <Toast variant={toast.variant}>{toast.message}</Toast>
      )}
    </>
  );
};

// ─── Test Tools Section ───────────────────────────────────────────────────────
// Admin-triggered simulation tools: each button independently fires a real
// backend endpoint with synthetic TEST-prefixed data (never touching
// Order/Stripe). All three endpoints respond 200 with { success, detail } on
// both the happy path AND a service-level failure -- the toast is keyed off
// that response body directly (mirrors XpyunPrintSection's fire-and-forget
// mutation + setTimeout-cleared Toast shape, not QueueCounterSection's
// useQuery-backed pattern, since there is nothing to fetch on mount here).
// `onError` only fires for a genuine transport-level failure (network error,
// timeout) -- never for a reported service failure, which arrives as a
// success: false 200 response instead.

const TestToolsSection: React.FC = () => {
  const { t } = useTranslation();
  const [testEmail, setTestEmail] = useState<string>('');
  const [testPhone, setTestPhone] = useState<string>('');
  const [phoneError, setPhoneError] = useState<boolean>(false);
  const [toast, setToast] = useState<{ variant: 'success' | 'error'; message: string } | null>(null);

  const showToast = (variant: 'success' | 'error', message: string) => {
    setToast({ variant, message });
    setTimeout(() => setToast(null), variant === 'success' ? 3000 : 4000);
  };

  const emailMutation = useMutation<TestActionResponse, unknown, { to_email?: string }>({
    mutationFn: async (payload) => {
      const res = await axiosInstance.post<TestActionResponse>('/admin/settings/test-email', payload);
      return res.data;
    },
    onSuccess: (data) => {
      showToast(data.success ? 'success' : 'error', data.detail);
    },
    onError: () => {
      showToast('error', t('admin.settings.testToolsGenericError', 'Something went wrong. Please try again.'));
    },
  });

  const smsMutation = useMutation<TestActionResponse, unknown, { to_phone: string }>({
    mutationFn: async (payload) => {
      const res = await axiosInstance.post<TestActionResponse>('/admin/settings/test-sms', payload);
      return res.data;
    },
    onSuccess: (data) => {
      showToast(data.success ? 'success' : 'error', data.detail);
    },
    onError: () => {
      showToast('error', t('admin.settings.testToolsGenericError', 'Something went wrong. Please try again.'));
    },
  });

  const printMutation = useMutation<TestActionResponse, unknown, void>({
    mutationFn: async () => {
      const res = await axiosInstance.post<TestActionResponse>('/admin/settings/test-print');
      return res.data;
    },
    onSuccess: (data) => {
      showToast(data.success ? 'success' : 'error', data.detail);
    },
    onError: () => {
      showToast('error', t('admin.settings.testToolsGenericError', 'Something went wrong. Please try again.'));
    },
  });

  const handleSendEmail = () => {
    const trimmed = testEmail.trim();
    emailMutation.mutate(trimmed ? { to_email: trimmed } : {});
  };

  const handleSendSms = () => {
    const trimmed = testPhone.trim();
    if (!trimmed) {
      setPhoneError(true);
      return;
    }
    setPhoneError(false);
    smsMutation.mutate({ to_phone: trimmed });
  };

  const handlePrint = () => {
    printMutation.mutate();
  };

  return (
    <>
      <ActionGroup>
        <FieldRow>
          <FieldLabel htmlFor="testEmail">
            {t('admin.settings.testToolsEmailLabel', 'Test Email Address')}
          </FieldLabel>
          <TextInput
            id="testEmail"
            type="email"
            placeholder={t('admin.settings.testToolsEmailPlaceholder', 'optional@example.com')}
            value={testEmail}
            onChange={e => setTestEmail(e.target.value)}
          />
        </FieldRow>
        <SaveButton onClick={handleSendEmail} disabled={emailMutation.isPending}>
          {emailMutation.isPending
            ? t('admin.settings.testToolsSendingEmailButton', 'Sending…')
            : t('admin.settings.testToolsSendEmailButton', 'Send Test Email')}
        </SaveButton>
      </ActionGroup>

      <ActionGroup>
        <FieldRow>
          <FieldLabel htmlFor="testPhone">
            {t('admin.settings.testToolsPhoneLabel', 'Test Phone Number')}
          </FieldLabel>
          <TextInput
            id="testPhone"
            type="tel"
            placeholder={t('admin.settings.testToolsPhonePlaceholder', '+1 555 123 4567')}
            value={testPhone}
            onChange={e => {
              setTestPhone(e.target.value);
              if (phoneError) setPhoneError(false);
            }}
          />
        </FieldRow>
        {phoneError && (
          <p style={{ color: '#dc2626', fontSize: '13px', marginTop: '-8px', marginBottom: '12px' }}>
            {t('admin.settings.testToolsPhoneRequiredError', 'Phone number is required to send a test SMS.')}
          </p>
        )}
        <SaveButton onClick={handleSendSms} disabled={smsMutation.isPending}>
          {smsMutation.isPending
            ? t('admin.settings.testToolsSendingSmsButton', 'Sending…')
            : t('admin.settings.testToolsSendSmsButton', 'Send Test SMS')}
        </SaveButton>
      </ActionGroup>

      <ActionGroup>
        <SaveButton onClick={handlePrint} disabled={printMutation.isPending}>
          {printMutation.isPending
            ? t('admin.settings.testToolsPrintingButton', 'Printing…')
            : t('admin.settings.testToolsPrintButton', 'Print Test Label')}
        </SaveButton>
      </ActionGroup>

      {toast && (
        <Toast variant={toast.variant}>{toast.message}</Toast>
      )}
    </>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────

const AdminSettings: React.FC = () => {
  const { t } = useTranslation();
  return (
    <Container>
      <PageTitle>{t('admin.settings.pageTitle')}</PageTitle>

      <Section>
        <SectionTitle>{t('admin.settings.queueCounterTitle')}</SectionTitle>
        <QueueCounterSection />
      </Section>

      <Section>
        <SectionTitle>{t('admin.settings.printLabelsTitle')}</SectionTitle>
        <XpyunPrintSection />
      </Section>

      <Section>
        <SectionTitle>{t('admin.settings.testToolsTitle', 'Simulation / Test Tools')}</SectionTitle>
        <TestToolsSection />
      </Section>
    </Container>
  );
};

export default AdminSettings;
